/**
 * Fundraiser Service
 *
 * Client-side service for Fund My Squad admin API endpoints.
 */

import { apiRequest } from '../config/api';

// --- Types ---

export interface FundraiserSettings {
  enabled: boolean;
  apiKeyConfigured: boolean;
  apiKeyMasked: string | null;
  apiBaseUrl: string;
}

export interface FundraiserSummary {
  totalFundraisers: number;
  activeFundraisers: number;
  totalRaised: number;
  totalDonations: number;
  averageDonation: number;
}

export interface FundraiserTracking {
  id: string;
  fmsId: string;
  externalId: string;
  entityId: string;
  entityName: string;
  entityType: string;
  title: string;
  status: string;
  goalAmount: number;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  fmsData?: FMSFundraiser | null;
  /**
   * Whether this viewer runs the campaign — creator, entity admin, or platform
   * admin. Stamped by the server, which is the only side that knows the auth
   * id behind `createdBy`. Gates the donor-level insights.
   */
  canManage?: boolean;
}

export interface FMSFundraiser {
  id: string;
  title: string;
  description?: string;
  goalAmount: number;
  totalRaised: number;
  donationCount: number;
  status: string;
  externalId: string;
  heroImageUrl?: string;
  publicUrl?: string;
  donateUrl?: string;
  startDate?: string;
  endDate?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Donation {
  id: string;
  amount: number;
  donorName: string;
  donorEmail?: string;
  message?: string;
  createdAt: string;
}

export interface FundraiserStats {
  totalRaised: number;
  donationCount: number;
  averageDonation: number;
  trend: Array<{ date: string; amount: number; count: number }>;
}

export interface ConnectStatus {
  connected: boolean;
  chargesEnabled: boolean;
  payoutsEnabled: boolean;
  detailsSubmitted: boolean;
  status?: string;
  connectAccountId?: string;
}

export interface CreateFundraiserPayload {
  entityId: string;
  entityName: string;
  entityType: string;
  title: string;
  description?: string;
  goalAmount: number;
  heroImageUrl?: string;
  startDate?: string;
  endDate?: string;
  // Donation vs product fundraiser
  type?: 'donation' | 'product';
  shopHeadline?: string;
  shopDescription?: string;
  // The chosen FMS product bundle (required when type === 'product')
  bundleId?: string;
  // Audience / hierarchical posting (approval governs visibility only)
  primaryEntityId?: string;
  hierarchicalPosting?: any;
}

export interface BundleProduct {
  id: string;
  title: string;
  description: string | null;
  // FMS returns image objects, e.g. [{ url: 'https://...' }]
  images: Array<{ url: string }>;
  minRetailPrice: number;
  shippingCost: number;
}

export interface FundraiserBundle {
  id: string;
  name: string;
  emoji?: string;
  description?: string;
  productCount: number;
  products: BundleProduct[];
}

export interface UpdateFundraiserPayload {
  title?: string;
  description?: string;
  goalAmount?: number;
  endDate?: string;
}

// --- Service ---

class FundraiserService {
  private basePath = '/api/fundraiser/admin';

  async getSettings(): Promise<FundraiserSettings> {
    const res = await apiRequest<{ success: boolean; data: FundraiserSettings }>(
      `${this.basePath}/settings`,
    );
    return res.data;
  }

  async updateSettings(payload: {
    enabled?: boolean;
    apiKey?: string;
    apiBaseUrl?: string;
  }): Promise<FundraiserSettings> {
    const res = await apiRequest<{ success: boolean; data: FundraiserSettings }>(
      `${this.basePath}/settings`,
      { method: 'PUT', body: JSON.stringify(payload) },
    );
    return res.data;
  }

  async getSummary(): Promise<FundraiserSummary> {
    const res = await apiRequest<{ success: boolean; data: FundraiserSummary }>(
      `${this.basePath}/summary`,
    );
    return res.data;
  }

  async listFundraisers(filters?: {
    entityId?: string;
    status?: string;
  }): Promise<FundraiserTracking[]> {
    const params = new URLSearchParams();
    if (filters?.entityId) params.append('entityId', filters.entityId);
    if (filters?.status) params.append('status', filters.status);
    const query = params.toString();
    const url = query ? `${this.basePath}/fundraisers?${query}` : `${this.basePath}/fundraisers`;
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking[] }>(url);
    return res.data;
  }

  async createFundraiser(payload: CreateFundraiserPayload): Promise<FundraiserTracking> {
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking }>(
      `${this.basePath}/fundraisers`,
      { method: 'POST', body: JSON.stringify(payload) },
    );
    return res.data;
  }

  async getCatalogBundles(): Promise<FundraiserBundle[]> {
    const res = await apiRequest<{ success: boolean; data: FundraiserBundle[] }>(
      `${this.basePath}/catalog/bundles`,
    );
    return res.data;
  }

  async getFundraiser(id: string): Promise<FundraiserTracking> {
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking }>(
      `${this.basePath}/fundraisers/${id}`,
    );
    return res.data;
  }

  async updateFundraiser(id: string, payload: UpdateFundraiserPayload): Promise<FMSFundraiser> {
    const res = await apiRequest<{ success: boolean; data: FMSFundraiser }>(
      `${this.basePath}/fundraisers/${id}`,
      { method: 'PATCH', body: JSON.stringify(payload) },
    );
    return res.data;
  }

  async closeFundraiser(id: string): Promise<void> {
    await apiRequest(`${this.basePath}/fundraisers/${id}/close`, { method: 'POST' });
  }

  async reopenFundraiser(id: string): Promise<void> {
    await apiRequest(`${this.basePath}/fundraisers/${id}/reopen`, { method: 'POST' });
  }

  async getDonations(fundraiserId: string): Promise<Donation[]> {
    const res = await apiRequest<{ success: boolean; data: Donation[] }>(
      `${this.basePath}/fundraisers/${fundraiserId}/donations`,
    );
    return res.data;
  }

  async getStats(fundraiserId: string): Promise<FundraiserStats> {
    const res = await apiRequest<{ success: boolean; data: FundraiserStats }>(
      `${this.basePath}/fundraisers/${fundraiserId}/stats`,
    );
    return res.data;
  }

  async startStripeOnboard(
    fundraiserId: string,
    returnUrl: string,
    refreshUrl: string,
  ): Promise<{ url: string }> {
    const res = await apiRequest<{ success: boolean; data: { url: string } }>(
      `${this.basePath}/fundraisers/${fundraiserId}/connect/onboard`,
      { method: 'POST', body: JSON.stringify({ returnUrl, refreshUrl }) },
    );
    return res.data;
  }

  async getConnectStatus(fundraiserId: string): Promise<ConnectStatus> {
    const res = await apiRequest<{ success: boolean; data: ConnectStatus }>(
      `${this.basePath}/fundraisers/${fundraiserId}/connect/status`,
    );
    return res.data;
  }

  // --- Entity-level reusable payout account (one per entity) ---

  async getEntityConnectStatus(entityId: string): Promise<ConnectStatus> {
    const res = await apiRequest<{ success: boolean; data: ConnectStatus }>(
      `${this.basePath}/connect/status?entityId=${encodeURIComponent(entityId)}`,
    );
    return res.data;
  }

  async onboardEntityPayouts(
    entityId: string,
    returnUrl: string,
    refreshUrl?: string,
  ): Promise<{ onboardingUrl?: string; status?: string; connectAccountId?: string }> {
    const res = await apiRequest<{
      success: boolean;
      data: { onboardingUrl?: string; status?: string; connectAccountId?: string };
    }>(`${this.basePath}/connect/onboard`, {
      method: 'POST',
      body: JSON.stringify({ entityId, returnUrl, refreshUrl }),
    });
    return res.data;
  }

  async linkFundraiserPayouts(
    fundraiserId: string,
    entityId?: string,
  ): Promise<{ success?: boolean }> {
    const res = await apiRequest<{ success: boolean; data: any }>(
      `${this.basePath}/fundraisers/${fundraiserId}/connect/link`,
      { method: 'POST', body: JSON.stringify({ entityId }) },
    );
    return res.data;
  }
}

export const fundraiserService = new FundraiserService();

// --- User-facing service (non-admin) ---

class FundraiserUserService {
  private basePath = '/api/fundraisers';

  async getStatus(): Promise<{ enabled: boolean }> {
    const res = await apiRequest<{ success: boolean; data: { enabled: boolean } }>(
      `${this.basePath}/settings/status`,
    );
    return res.data;
  }

  async listMyFundraisers(): Promise<FundraiserTracking[]> {
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking[] }>(this.basePath);
    return res.data;
  }

  async getFundraiser(id: string): Promise<FundraiserTracking> {
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking }>(
      `${this.basePath}/${id}`,
    );
    return res.data;
  }

  async createFundraiser(payload: CreateFundraiserPayload): Promise<FundraiserTracking> {
    const res = await apiRequest<{ success: boolean; data: FundraiserTracking }>(this.basePath, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return res.data;
  }

  async getDonations(fundraiserId: string): Promise<Donation[]> {
    const res = await apiRequest<{ success: boolean; data: Donation[] }>(
      `${this.basePath}/${fundraiserId}/donations`,
    );
    return res.data;
  }

  async getStats(fundraiserId: string): Promise<FundraiserStats> {
    const res = await apiRequest<{ success: boolean; data: FundraiserStats }>(
      `${this.basePath}/${fundraiserId}/stats`,
    );
    return res.data;
  }
}

export const fundraiserUserService = new FundraiserUserService();
